import { ThemeProvider as MuiThemeProvider, StyledEngineProvider, Theme } from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import { BusinessUnitKeyType, RouteName, AppRoutes as Routes } from 'common';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Switch } from 'react-router-dom';
import { CustomGlobalStyles } from '../CustomGlobalStyles';
import { CfPageLoader } from '../cf-ui/Loader/PageLoader/CfPageLoader';
import Communications from '../features/account/views/Communications';
import ManageAccess from '../features/manage-access/view/ManageAccess';
import MenuCustomerDiary from '../features/recipes/views/MenuCustomerDiary';
import { getTheme } from '../theme/theme';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TitledRoute } from './components/TitledRouted';

const InventoryValuation = lazy(() => import('../features/inventory/views/InventoryValuation'));
const InventoryConsumption = lazy(() => import('../features/inventory/views/InventoryConsumption'));
const InventoryCostAnalysis = lazy(() => import('../features/inventory/views/InventoryCostAnalysis'));
const InventoryReports = lazy(() => import('../features/inventory/views/InventoryReports'));
const InventoryWorksheet = lazy(() => import('../features/inventory/views/InventoryWorksheet'));
const InventorySnapshot = lazy(() => import('../features/inventory/views/InventorySnapshot'));

const Home = lazy(() => import('../features/site/views/Home'));
const GetStarted = lazy(() => import('../features/site/views/GetStarted'));

const Terms = lazy(() => import('../features/user-acceptance/views/Terms'));
const Policy = lazy(() => import('../features/user-acceptance/views/Policy'));
const HumanVerification = lazy(() => import('../features/user-acceptance/views/HumanVerification'));
const TermsOfService = lazy(() => import('../features/user-acceptance/views/TermsOfService'));
const Cookies = lazy(() => import('../features/user-acceptance/views/Cookies'));
const CustomerSupport = lazy(() => import('../features/account/views/CustomerSupport'));
const ManageUsers = lazy(() => import('../features/account/views/ManageUsers'));
const ManageUsersExport = lazy(() => import('../features/account/views/ManageUsersExport/ManageUsersExport'));
const DeliveryDetails = lazy(() => import('../features/deliveries/views/DeliveryDetails/DeliveryDetails'));
const ProductList = lazy(() => import('../features/product-list-management/views/ProductList/ProductList'));
const ProductListCatalogSearch = lazy(
  () => import('../features/product-list-management/views/ProductListCatalogSearch/ProductListCatalogSearch')
);
const NoUserInformation = lazy(() => import('../features/user-acceptance/views/NoUserInformation'));
const Invoices = lazy(() => import('../features/finances/views/Invoices'));
const ProductListManagement = lazy(() => import('../features/product-list-management/views/ProductListManagement'));
const ProductListNotifications = lazy(
  () => import('../features/product-list-management/views/ProductListNotifications/ProductListNotifications')
);
const ProductListImport = lazy(
  () => import('../features/product-list-management/views/ProductListImport/ProductListImport')
);
const ProductListReport = lazy(
  () => import('../features/product-list-management/views/ProductListExport/ProductListExport')
);
const ProductListDiary = lazy(
  () => import('../features/product-list-management/views/ProductListDiary/ProductListDiary')
);
const OrderImport = lazy(() => import('../features/orders/views/OrderImport/OrderImport'));
const OrderDetails = lazy(() => import('../features/orders/views/OrderDetails/OrderDetails'));
const OrderEntry = lazy(() => import('../features/orders/views/OrderEntry/OrderEntry'));
const OrderReview = lazy(() => import('../features/orders/views/OrderReview/OrderReview'));
const OrderHistory = lazy(() => import('../features/orders/views/Orders/OrderHistory'));
const Search = lazy(() => import('../features/orders/views/CatalogSearch/CatalogSearch'));
const SearchSimilar = lazy(() => import('../features/orders/views/FindSimilar/SimilarProductSearch'));
const OrderConfirmation = lazy(() => import('../features/orders/views/OrderConfirmation/OrderConfirmation'));
const QuickView = lazy(() => import('../features/orders/views/ProductDetails/ProductDetails'));
const SearchAnalysis = lazy(() => import('../features/_experimental/views/SearchAnalysis/SearchAnalysis'));
const UI = lazy(() => import('../features/_experimental/views/UI'));
const VideoUI = lazy(() => import('../features/_experimental/views/Video/VideoUI'));
const UserProfile = lazy(() => import('../features/account/views/UserProfile'));
const CreateMessage = lazy(() => import('../features/account/views/CreateMessage'));
const MessageGroups = lazy(() => import('../features/account/views/MessageGroups'));
const Messages = lazy(() => import('../features/account/views/Messages'));
const CustomerGroups = lazy(() => import('../features/account/views/CustomerGroups'));
const DocumentsLibrary = lazy(() => import('../features/account/views/DocumentsLibrary'));

const VerifyContactEmail = lazy(() => import('../features/account/views/VerifyContactEmail'));
const LinkExpired = lazy(() => import('../features/site/views/LinkExpired'));
const InventoryManagement = lazy(() => import('../features/inventory/views/InventoryManagement'));
const InventoryEntry = lazy(() => import('../features/inventory/views/InventoryEntry'));
const InventoryQuickAdd = lazy(() => import('../features/inventory/views/InventoryQuickAdd'));

const InvoiceDetail = lazy(() => import('../features/finances/views/InvoiceDetail'));
const Insights = lazy(() => import('../features/finances/views/Insights'));
const InsightsReport = lazy(() => import('../features/finances/views/InsightsReport'));
const ADB2CLoginRedirect = lazy(() => import('./ADB2CLoginRedirect'));
const Dnd = lazy(() => import('../features/_experimental/views/Dnd/Dnd'));
const AccountReceivableStatements = lazy(
  () => import('../features/finances/views/AccountsReceivableStatements/AccountsReceivableStatements')
);
const AddFromList = lazy(() => import('../features/product-list-management/views/AddFromList'));
const ParManagement = lazy(() => import('../features/par-management/views/ParManagement'));
const ParMaintenance = lazy(() => import('../features/par-management/views/ParMaintenance'));
const VendorManagement = lazy(() => import('../features/vendor-management/views/VendorManagement'));
const VendorDetails = lazy(() => import('../features/vendor-management/views/VendorDetailsView'));
const ProductDetailsView = lazy(
  () => import('../features/vendor-management/third-party-product-management/views/ProductDetailsView')
);
const ProductDetailsSales = lazy(() => import('../features/orders/views/ProductDetails/ProductDetailsSales'));
const AddFromVendor = lazy(() => import('../features/vendor-management/views/AddFromVendor'));
const VendorInvoices = lazy(() => import('../features/vendor-management/third-party-invoice/views/VendorInvoices'));
const VendorInvoiceDetails = lazy(
  () => import('../features/vendor-management/third-party-invoice/views/VendorInvoiceDetails')
);
const AddVendorInvoiceProducts = lazy(
  () => import('../features/vendor-management/third-party-invoice/views/AddVendorInvoiceProducts')
);
const VendorDiary = lazy(() => import('../features/vendor-management/views/ThirdPartyVendorDiary'));
const SmartOrder = lazy(() => import('../features/orders/views/SmartOrder/SmartOrder'));

const RecipeDashboard = lazy(() => import('../features/recipes/views/RecipeDashboard'));
const BrowseRecipes = lazy(() => import('../features/recipes/views/BrowseRecipes'));
const BrowseRecipesByCategory = lazy(() => import('../features/recipes/views/BrowseRecipesByCategory'));
const Recipes = lazy(() => import('../features/recipes/views/Recipes'));
const RecipeDetails = lazy(() => import('../features/recipes/views/RecipeDetails'));
const RecipeDiary = lazy(() => import('../features/recipes/views/RecipeDiary'));
const RecipeCustomerDiary = lazy(() => import('../features/recipes/views/RecipeCustomerDiary'));
const AddFromIngredients = lazy(() => import('../features/recipes/views/AddFromIngredients'));
const AddFromRecipes = lazy(() => import('../features/recipes/views/AddFromRecipes'));
const RecipesCatalogSearch = lazy(() => import('../features/recipes/views/RecipesCatalogSearch'));
const Ingredients = lazy(() => import('../features/recipes/views/Ingredients'));
const IngredientDetails = lazy(() => import('../features/recipes/views/IngredientDetails'));
const IngredientAddFromVendor = lazy(() => import('../features/recipes/views/IngredientAddFromVendor'));
const IngredientAddFromList = lazy(() => import('../features/recipes/views/IngredientAddFromList'));
const IngredientAddProducts = lazy(() => import('../features/recipes/views/IngredientAddProducts'));
const IngredientsCustomerDiary = lazy(() => import('../features/recipes/views/IngredientCustomerDiary'));
const IngredientsDiary = lazy(() => import('../features/recipes/views/IngredientsDiary'));
const Menus = lazy(() => import('../features/recipes/views/Menus'));
const MenuDetails = lazy(() => import('../features/recipes/views/MenuDetails'));
const MenuDiary = lazy(() => import('../features/recipes/views/MenuDiary'));
const MenuWorksheets = lazy(() => import('../features/recipes/views/MenuWorksheets'));
const MenuWorksheetDetails = lazy(() => import('../features/recipes/views/MenuWorksheetDetails'));
const RecipeReports = lazy(() => import('../features/recipes/views/RecipeReports'));
const RecipeCosting = lazy(() => import('../features/recipes/views/RecipeCosting'));
const RecipeMenuCosting = lazy(() => import('../features/recipes/views/RecipeMenuCosting'));
const RecipeNotifications = lazy(() => import('../features/recipes/views/RecipeNotifications'));
const RecipeDetailReport = lazy(() => import('../features/recipes/views/RecipeDetailReport'));
const ProductCatalogConversationDetails = lazy(
  () => import('../features/orders/views/ProductCatalogConversation/ProductCatalogConversationDetails')
);
const ProductRecommendations = lazy(
  () => import('../features/customer-product-recommendations/views/CustomerProductRecommendations')
);
const CategorySearch = lazy(() => import('../features/product-shopping-categories/views/CategorySearch'));

export const AppRouting = (): JSX.Element => {
  return (
    <Switch>
      <TitledRoute route={RouteName.Home} path={getPath(RouteName.Home)} component={Home} exact />

      {/* Search */}
      <TitledRoute route={RouteName.ProductSearch} path={getPath(RouteName.ProductSearch)} component={Search} />
      <TitledRoute
        route={RouteName.SimilarProductSearch}
        path={getPath(RouteName.SimilarProductSearch)}
        component={SearchSimilar}
      />
      <TitledRoute route={RouteName.ProductDetails} path={getPath(RouteName.ProductDetails)} component={QuickView} />
      <TitledRoute
        route={RouteName.CategorySearch}
        path={getPath(RouteName.CategorySearch)}
        component={CategorySearch}
      />

      {/* User */}
      <TitledRoute
        route={RouteName.TermsOfService}
        path={getPath(RouteName.TermsOfService)}
        component={TermsOfService}
      />
      <TitledRoute route={RouteName.CookieAcceptance} path={getPath(RouteName.CookieAcceptance)} component={Cookies} />
      <TitledRoute
        route={RouteName.NoUserInformation}
        path={getPath(RouteName.NoUserInformation)}
        component={NoUserInformation}
        exact
      />
      <TitledRoute
        route={RouteName.VerifyContactEmail}
        exact
        path={getPath(RouteName.VerifyContactEmail)}
        component={VerifyContactEmail}
      />
      <TitledRoute route={RouteName.UserProfile} path={getPath(RouteName.UserProfile)} component={UserProfile} />
      <TitledRoute
        route={RouteName.HumanVerification}
        path={getPath(RouteName.HumanVerification)}
        component={HumanVerification}
      />

      {/* Orders */}
      <ProtectedRoute route={RouteName.OrderHistory} path={getPath(RouteName.OrderHistory)} component={OrderHistory} />
      <ProtectedRoute route={RouteName.RepeatOrder} path={getPath(RouteName.RepeatOrder)} component={OrderHistory} />

      <ProtectedRoute route={RouteName.OrderEntry} path={getPath(RouteName.OrderEntry)} component={OrderEntry} />
      <ProtectedRoute
        route={RouteName.OrderDetails}
        path={getPath(RouteName.OrderDetails)}
        component={OrderDetails}
        exact
      />
      <ProtectedRoute route={RouteName.OrderReview} path={getPath(RouteName.OrderReview)} component={OrderReview} />
      <ProtectedRoute
        route={RouteName.OrderConfirmation}
        path={getPath(RouteName.OrderConfirmation)}
        component={OrderConfirmation}
      />
      <ProtectedRoute
        route={RouteName.DeliveryDetails}
        path={getPath(RouteName.DeliveryDetails)}
        component={DeliveryDetails}
        exact
      />
      <ProtectedRoute route={RouteName.OrderImport} path={getPath(RouteName.OrderImport)} component={OrderImport} />
      <ProtectedRoute route={RouteName.SmartOrder} path={getPath(RouteName.SmartOrder)} component={SmartOrder} />

      {/* Message Center */}
      <ProtectedRoute
        route={RouteName.CreateMessage}
        path={Routes[RouteName.CreateMessage].Path}
        component={CreateMessage}
      />
      <TitledRoute
        route={RouteName.ViewInboxMessage}
        path={Routes[RouteName.ViewInboxMessage].Path}
        component={Messages}
      />
      <TitledRoute route={RouteName.InboxMessages} path={Routes[RouteName.InboxMessages].Path} component={Messages} />

      <ProtectedRoute
        route={RouteName.ViewDraftMessage}
        path={Routes[RouteName.ViewDraftMessage].Path}
        component={Messages}
      />
      <ProtectedRoute
        route={RouteName.DraftMessages}
        path={Routes[RouteName.DraftMessages].Path}
        component={Messages}
      />

      <ProtectedRoute
        route={RouteName.ViewSentMessage}
        path={Routes[RouteName.ViewSentMessage].Path}
        component={Messages}
      />
      <ProtectedRoute route={RouteName.SentMessages} path={Routes[RouteName.SentMessages].Path} component={Messages} />

      <ProtectedRoute
        route={RouteName.UpdateMessageGroup}
        path={Routes[RouteName.UpdateMessageGroup].Path}
        component={MessageGroups}
      />
      <ProtectedRoute
        route={RouteName.CreateMessageGroup}
        path={Routes[RouteName.CreateMessageGroup].Path}
        component={MessageGroups}
      />
      <ProtectedRoute
        route={RouteName.ViewMessageGroup}
        path={Routes[RouteName.ViewMessageGroup].Path}
        component={MessageGroups}
      />

      <ProtectedRoute
        route={RouteName.MessageGroups}
        path={Routes[RouteName.MessageGroups].Path}
        component={MessageGroups}
      />

      {/* Communications */}
      <TitledRoute
        route={RouteName.Communications}
        path={Routes[RouteName.Communications].Path}
        component={Communications}
      />

      {/* Documents Library */}
      <ProtectedRoute
        route={RouteName.DocumentLibrary}
        path={Routes[RouteName.DocumentLibrary].Path}
        component={DocumentsLibrary}
      />

      {/* Manage Users */}
      <ProtectedRoute
        route={RouteName.ManageUsersExport}
        path={Routes[RouteName.ManageUsersExport].Path}
        component={ManageUsersExport}
      />
      <ProtectedRoute route={RouteName.ManageUsers} path={Routes[RouteName.ManageUsers].Path} component={ManageUsers} />

      {/* Customer Support */}
      <TitledRoute
        route={RouteName.CustomerSupport}
        path={getPath(RouteName.CustomerSupport)}
        component={CustomerSupport}
        exact
      />
      <TitledRoute
        route={RouteName.AccountTutorials}
        path={getPath(RouteName.AccountTutorials)}
        component={CustomerSupport}
      />
      <TitledRoute
        route={RouteName.OrderingTutorials}
        path={getPath(RouteName.OrderingTutorials)}
        component={CustomerSupport}
      />
      <TitledRoute
        route={RouteName.InvoiceTutorials}
        path={getPath(RouteName.InvoiceTutorials)}
        component={CustomerSupport}
      />
      <TitledRoute
        route={RouteName.ListManagementTutorials}
        path={getPath(RouteName.ListManagementTutorials)}
        component={CustomerSupport}
      />
      <TitledRoute
        route={RouteName.ParManagementTutorials}
        path={getPath(RouteName.ParManagementTutorials)}
        component={CustomerSupport}
      />
      <TitledRoute
        route={RouteName.RecipeTutorials}
        path={getPath(RouteName.RecipeTutorials)}
        component={CustomerSupport}
      />

      {/* Customer Groups */}
      <ProtectedRoute
        route={RouteName.CreateCustomerGroup}
        path={Routes[RouteName.CreateCustomerGroup].Path}
        component={CustomerGroups}
      />

      <ProtectedRoute
        route={RouteName.ViewCustomerGroup}
        path={Routes[RouteName.ViewCustomerGroup].Path}
        component={CustomerGroups}
      />

      <ProtectedRoute
        route={RouteName.CustomerGroups}
        path={Routes[RouteName.CustomerGroups].Path}
        component={CustomerGroups}
      />

      {/* List Management*/}
      <ProtectedRoute
        route={RouteName.ProductListDiary}
        path={Routes[RouteName.ProductListDiary].Path}
        component={ProductListDiary}
      />
      <ProtectedRoute
        route={RouteName.ProductListExport}
        path={Routes[RouteName.ProductListExport].Path}
        component={ProductListReport}
        exact
      />
      <ProtectedRoute
        route={RouteName.ProductListCatalogSearch}
        path={getPath(RouteName.ProductListCatalogSearch)}
        component={ProductListCatalogSearch}
      />
      {/* Add From Vendor */}
      <ProtectedRoute
        route={RouteName.AddFromVendor}
        path={getPath(RouteName.AddFromVendor)}
        component={AddFromVendor}
      />
      <ProtectedRoute route={RouteName.AddFromList} path={getPath(RouteName.AddFromList)} component={AddFromList} />
      <ProtectedRoute route={RouteName.ProductList} path={getPath(RouteName.ProductList)} component={ProductList} />
      <ProtectedRoute
        route={RouteName.ProductListImport}
        path={getPath(RouteName.ProductListImport)}
        component={ProductListImport}
        exact={true}
      />
      <ProtectedRoute
        route={RouteName.ProductListManagement}
        path={getPath(RouteName.ProductListManagement)}
        component={ProductListManagement}
      />
      <ProtectedRoute
        route={RouteName.ProductListNotifications}
        path={getPath(RouteName.ProductListNotifications)}
        component={ProductListNotifications}
      />

      {/* Finance */}
      <ProtectedRoute
        route={RouteName.AccountsReceivables}
        path={getPath(RouteName.AccountsReceivables)}
        component={AccountReceivableStatements}
        exact
      />
      <ProtectedRoute route={RouteName.Invoices} path={getPath(RouteName.Invoices)} component={Invoices} />
      <ProtectedRoute
        route={RouteName.InvoiceDetail}
        exact
        path={getPath(RouteName.InvoiceDetail)}
        component={InvoiceDetail}
      />
      <ProtectedRoute route={RouteName.Insights} exact path={getPath(RouteName.Insights)} component={Insights} />
      <ProtectedRoute
        route={RouteName.InsightsReport}
        path={getPath(RouteName.InsightsReport)}
        component={InsightsReport}
      />

      {/* Inventory */}
      <ProtectedRoute
        route={RouteName.InventoryReports}
        path={getPath(RouteName.InventoryReports)}
        component={InventoryReports}
      />
      <ProtectedRoute
        route={RouteName.InventoryCostAnalysis}
        path={getPath(RouteName.InventoryCostAnalysis)}
        component={InventoryCostAnalysis}
      />
      <ProtectedRoute
        route={RouteName.InventoryQuickAdd}
        path={getPath(RouteName.InventoryQuickAdd)}
        component={InventoryQuickAdd}
      />
      <ProtectedRoute
        route={RouteName.InventoryConsumption}
        path={getPath(RouteName.InventoryConsumption)}
        component={InventoryConsumption}
      />
      <ProtectedRoute
        route={RouteName.InventoryValuation}
        path={getPath(RouteName.InventoryValuation)}
        component={InventoryValuation}
      />
      <ProtectedRoute
        route={RouteName.InventorySnapshot}
        path={getPath(RouteName.InventorySnapshot)}
        component={InventorySnapshot}
      />
      <ProtectedRoute
        route={RouteName.InventoryWorksheet}
        path={getPath(RouteName.InventoryWorksheet)}
        component={InventoryWorksheet}
      />
      <ProtectedRoute
        route={RouteName.InventoryEntry}
        path={getPath(RouteName.InventoryEntry)}
        component={InventoryEntry}
      />
      <ProtectedRoute
        route={RouteName.InventoryManagement}
        path={getPath(RouteName.InventoryManagement)}
        component={InventoryManagement}
      />
      <ProtectedRoute
        route={RouteName.ParManagement}
        path={getPath(RouteName.ParManagement)}
        component={ParManagement}
        exact
      />
      <ProtectedRoute
        route={RouteName.ParMaintenance}
        path={getPath(RouteName.ParMaintenance)}
        component={ParMaintenance}
      />

      {/* Vendor Product Details */}
      <ProtectedRoute
        route={RouteName.VendorProductDetail}
        path={getPath(RouteName.VendorProductDetail)}
        exact={true}
        component={ProductDetailsView}
      />

      {/* Third Party Invoice Details */}
      <ProtectedRoute
        route={RouteName.VendorInvoiceDetails}
        path={getPath(RouteName.VendorInvoiceDetails)}
        exact={true}
        component={VendorInvoiceDetails}
      />
      <ProtectedRoute
        route={RouteName.AddVendorInvoiceProducts}
        path={getPath(RouteName.AddVendorInvoiceProducts)}
        component={AddVendorInvoiceProducts}
        exact
      />
      {/* Vendor Management */}
      <ProtectedRoute
        route={RouteName.VendorManagement}
        path={getPath(RouteName.VendorManagement)}
        exact={true}
        component={VendorManagement}
      />

      <ProtectedRoute
        route={RouteName.VendorDiary}
        path={getPath(RouteName.VendorDiary)}
        exact={true}
        component={VendorDiary}
      />
      {/* Vendor Details */}
      <ProtectedRoute
        route={RouteName.VendorDetails}
        path={getPath(RouteName.VendorDetails)}
        component={VendorDetails}
      />
      {/* Recipes */}
      <ProtectedRoute
        route={RouteName.RecipeDashboard}
        path={getPath(RouteName.RecipeDashboard)}
        component={RecipeDashboard}
        exact
      />
      <ProtectedRoute
        route={RouteName.BrowseRecipes}
        path={getPath(RouteName.BrowseRecipes)}
        component={BrowseRecipes}
        exact
      />
      <ProtectedRoute
        route={RouteName.BrowseRecipesByCategory}
        path={getPath(RouteName.BrowseRecipesByCategory)}
        component={BrowseRecipesByCategory}
        exact
      />
      <ProtectedRoute route={RouteName.Recipes} path={getPath(RouteName.Recipes)} component={Recipes} exact />
      <ProtectedRoute
        route={RouteName.RecipeDiary}
        path={getPath(RouteName.RecipeDiary)}
        component={RecipeDiary}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeCustomerDiary}
        path={getPath(RouteName.RecipeCustomerDiary)}
        component={RecipeCustomerDiary}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeDetails}
        path={getPath(RouteName.RecipeDetails)}
        component={RecipeDetails}
        exact
      />
      <ProtectedRoute
        route={RouteName.AddFromRecipes}
        path={getPath(RouteName.AddFromRecipes)}
        component={AddFromRecipes}
        exact
      />
      <ProtectedRoute
        route={RouteName.AddFromIngredients}
        path={getPath(RouteName.AddFromIngredients)}
        component={AddFromIngredients}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeCatalogSearch}
        path={getPath(RouteName.RecipeCatalogSearch)}
        component={RecipesCatalogSearch}
        exact
      />
      <ProtectedRoute
        route={RouteName.Ingredients}
        path={getPath(RouteName.Ingredients)}
        component={Ingredients}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientsCustomerDiary}
        path={getPath(RouteName.IngredientsCustomerDiary)}
        component={IngredientsCustomerDiary}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientDiary}
        path={getPath(RouteName.IngredientDiary)}
        component={IngredientsDiary}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientDetails}
        path={getPath(RouteName.IngredientDetails)}
        component={IngredientDetails}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientAddFromVendor}
        path={getPath(RouteName.IngredientAddFromVendor)}
        component={IngredientAddFromVendor}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientAddFromList}
        path={getPath(RouteName.IngredientAddFromList)}
        component={IngredientAddFromList}
        exact
      />
      <ProtectedRoute
        route={RouteName.IngredientAddProducts}
        path={getPath(RouteName.IngredientAddProducts)}
        component={IngredientAddProducts}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeReports}
        path={getPath(RouteName.RecipeReports)}
        component={RecipeReports}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeCosting}
        path={getPath(RouteName.RecipeCosting)}
        component={RecipeCosting}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeMenuCosting}
        path={getPath(RouteName.RecipeMenuCosting)}
        component={RecipeMenuCosting}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeNotifications}
        path={getPath(RouteName.RecipeNotifications)}
        component={RecipeNotifications}
        exact
      />
      <ProtectedRoute
        route={RouteName.RecipeDetailReport}
        path={getPath(RouteName.RecipeDetailReport)}
        component={RecipeDetailReport}
        exact
      />
      {/* Menu Pages */}
      <ProtectedRoute route={RouteName.Menus} path={getPath(RouteName.Menus)} component={Menus} exact />
      <ProtectedRoute
        route={RouteName.MenuDetails}
        path={getPath(RouteName.MenuDetails)}
        component={MenuDetails}
        exact
      />

      <ProtectedRoute
        route={RouteName.MenuWorksheets}
        path={getPath(RouteName.MenuWorksheets)}
        component={MenuWorksheets}
        exact
      />
      <ProtectedRoute
        route={RouteName.MenuWorksheetDetails}
        path={getPath(RouteName.MenuWorksheetDetails)}
        component={MenuWorksheetDetails}
        exact
      />
      <TitledRoute
        route={RouteName.ManageAccess}
        path={getPath(RouteName.ManageAccess)}
        component={ManageAccess}
        exact
      />
      <ProtectedRoute route={RouteName.MenuDiary} path={getPath(RouteName.MenuDiary)} component={MenuDiary} exact />
      <ProtectedRoute
        route={RouteName.MenuCustomerDiary}
        path={getPath(RouteName.MenuCustomerDiary)}
        component={MenuCustomerDiary}
        exact
      />

      {/* Third Party Invoice Listing */}
      <ProtectedRoute
        route={RouteName.VendorInvoices}
        path={getPath(RouteName.VendorInvoices)}
        component={VendorInvoices}
      />

      {/* AI */}
      <TitledRoute
        route={RouteName.ProductCatalogConversationDetails}
        path={getPath(RouteName.ProductCatalogConversationDetails)}
        component={ProductCatalogConversationDetails}
        exact
      />

      {/* Customer Product Recommendations */}
      <ProtectedRoute
        route={RouteName.ProductRecommendations}
        path={getPath(RouteName.ProductRecommendations)}
        component={ProductRecommendations}
        exact
      />

      {/* Landing pages */}
      <TitledRoute route={RouteName.GetStarted} path={getPath(RouteName.GetStarted)} component={GetStarted} />

      {/* Product Detail Sales Authorized Users */}

      <TitledRoute
        route={RouteName.ProductDetailsSales}
        path={getPath(RouteName.ProductDetailsSales)}
        component={ProductDetailsSales}
      />

      {/* Enviornment Related */}
      {process.env.REACT_APP_ENV_NAME !== 'prod' && (
        <TitledRoute
          route={RouteName.SearchAnalysis}
          path={getPath(RouteName.SearchAnalysis)}
          component={SearchAnalysis}
        />
      )}
      {(process.env.REACT_APP_ENV_NAME === 'local' || process.env.REACT_APP_ENV_NAME === 'dev') && (
        <TitledRoute route={RouteName.Video} path={getPath(RouteName.Video)} component={VideoUI} />
      )}
      {(process.env.REACT_APP_ENV_NAME === 'local' || process.env.REACT_APP_ENV_NAME === 'dev') && (
        <TitledRoute route={RouteName.UI} path={getPath(RouteName.UI)} component={UI} />
      )}
      {(process.env.REACT_APP_ENV_NAME === 'local' || process.env.REACT_APP_ENV_NAME === 'dev') && (
        <TitledRoute route={RouteName.DND} path={getPath(RouteName.DND)} component={Dnd} />
      )}
    </Switch>
  );
};

export const UnauthenticatedAppRouting = (): JSX.Element => {
  const [theme, setTheme] = useState<Theme | undefined>(undefined);

  useEffect(() => {
    setTheme(getTheme(BusinessUnitKeyType.PerformanceFoodService));
  }, []);

  return (
    <StylesProvider injectFirst>
      {theme && (
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={theme}>
            <CustomGlobalStyles />
            <Suspense fallback={<CfPageLoader isLoading />}>
              <Switch>
                <TitledRoute
                  route={RouteName.GetStarted}
                  path={Routes[RouteName.GetStarted].Path}
                  component={GetStarted}
                />
                <TitledRoute
                  route={RouteName.VerifyContactEmail}
                  exact
                  path={Routes[RouteName.VerifyContactEmail].Path}
                  component={VerifyContactEmail}
                />
                <TitledRoute
                  route={RouteName.LinkExpired}
                  path={Routes[RouteName.LinkExpired].Path}
                  component={LinkExpired}
                />
                <TitledRoute
                  route={RouteName.ProductDetailsSales}
                  path={getPath(RouteName.ProductDetailsSales)}
                  component={ProductDetailsSales}
                />
                <TitledRoute route={RouteName.Terms} path={getPath(RouteName.Terms)} component={Terms} />
                <TitledRoute route={RouteName.Policy} path={getPath(RouteName.Policy)} component={Policy} />
                <TitledRoute
                  route={RouteName.Login}
                  path={Routes[RouteName.Login].Path}
                  component={ADB2CLoginRedirect}
                />
              </Switch>
            </Suspense>
          </MuiThemeProvider>
        </StyledEngineProvider>
      )}
    </StylesProvider>
  );
};

const getPath = (route: RouteName) => {
  const r = Routes[route];
  let result = r.Path;
  if (r.Params?.length ?? 0 > 0) result = `${Routes[route].Path}/${Routes[route].Params?.join('/')}`;

  return result;
};
